import { Controller } from "@hotwired/stimulus"

// Builds a sticky table-of-contents sidebar for blog post pages.
// Reads h2 headings from the content target, renders links, scroll-spies the active section.
export default class extends Controller {
  static targets = ["content", "list"]

  connect() {
    this.headings = []
    this.build()
    this.handler = this.spy.bind(this)
    window.addEventListener("scroll", this.handler, { passive: true })
    this.spy()
  }

  disconnect() {
    window.removeEventListener("scroll", this.handler)
  }

  build() {
    if (!this.hasContentTarget || this.listTargets.length === 0) return

    this.removeInlineToc()

    const headings = this.contentTarget.querySelectorAll("h2")
    if (headings.length === 0) {
      this.element.classList.add("blog-toc-empty")
      return
    }

    this.headings = Array.from(headings).map((h, i) => {
      const id = h.id || `section-${i}`
      h.id = id
      h.style.scrollMarginTop = "6rem"
      return { id, text: h.textContent.trim() }
    })

    const html = this.headings
      .map(
        (h) =>
          `<a href="#${h.id}" data-action="click->blog-toc#go" data-id="${h.id}"
             class="blog-toc-link block py-2.5 ps-4 pe-2 text-[15px] leading-snug transition-colors text-base-content/55 hover:text-base-content no-underline border-s-[3px] border-transparent">
             ${h.text}
           </a>`
      )
      .join("")

    this.listTargets.forEach((nav) => { nav.innerHTML = html })
  }

  // Authors paste a "Table of Contents" block (h2 + ul of anchor links) into
  // the article body. The sticky sidebar already shows the TOC, so strip the
  // inline copy. Locale-independent: identifies the block by structure — an h2
  // immediately followed by a list whose first link is an in-page anchor.
  removeInlineToc() {
    const h2s = this.contentTarget.querySelectorAll("h2")
    for (const h2 of h2s) {
      const list = h2.nextElementSibling
      if (!list) continue
      if (list.tagName !== "UL" && list.tagName !== "OL") continue

      const firstLink = list.querySelector("a[href]")
      if (!firstLink) continue
      const href = firstLink.getAttribute("href") || ""
      if (!href.startsWith("#")) continue

      list.remove()
      h2.remove()
      return
    }
  }

  go(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.id
    const el = document.getElementById(id)
    if (!el) return

    const top = el.getBoundingClientRect().top + window.scrollY - 80
    window.scrollTo({ top, behavior: "smooth" })
  }

  spy() {
    if (this.headings.length === 0) return

    const offset = 120
    let activeId = this.headings[0].id
    for (const h of this.headings) {
      const el = document.getElementById(h.id)
      if (!el) continue
      if (el.getBoundingClientRect().top <= offset) activeId = h.id
    }

    this.listTargets.forEach((nav) => {
      nav.querySelectorAll(".blog-toc-link").forEach((link) => {
        const isActive = link.dataset.id === activeId
        link.classList.toggle("text-primary", isActive)
        link.classList.toggle("font-semibold", isActive)
        link.classList.toggle("border-primary", isActive)
        link.classList.toggle("border-transparent", !isActive)
        link.classList.toggle("text-base-content/55", !isActive)
      })
    })
  }
}
